import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Package, ArrowLeft, Eye } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function OrdersPage() {
  const { user, loading: authLoading, login } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      login();
    } else if (user) {
      fetchOrders();
    }
    // `login` and `fetchOrders` are stable component-scoped fns; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(response.data.orders || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "shipped":
        return "bg-blue-100 text-blue-700";
      case "delivered":
        return "bg-purple-100 text-purple-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="h-32 bg-gray-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link
              to="/directory"
              className="inline-flex items-center text-gray-600 hover:text-tbc-navy font-body mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Continue Shopping
            </Link>
            <h1 className="font-heading text-3xl text-tbc-navy">Your Orders</h1>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="font-heading text-xl text-tbc-navy mb-2">No orders yet</h2>
              <p className="text-gray-600 font-body mb-6">
                Start shopping to support Black-owned businesses
              </p>
              <Link to="/directory">
                <Button
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                  data-testid="shop-now"
                >
                  Shop Now
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  className="bg-white border border-gray-100 rounded-xl p-6"
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-500 font-body">
                        Order #{order.order_id.slice(-8).toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-400 font-body">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={`${order.order_id}-item-${item.product_id || idx}`} className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={`https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=100&q=80`}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-tbc-navy">{item.product_name}</p>
                          <p className="text-gray-500 text-sm font-body">
                            {item.business_name} · Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="font-heading text-tbc-navy">
                          ${item.subtotal.toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 mt-4 pt-4 flex items-center justify-between">
                    <span className="text-gray-600 font-body">Total</span>
                    <span className="font-heading text-xl text-tbc-navy">
                      ${order.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
